import { Inject, Injectable } from '@angular/core';
import { Model, ModelFactory } from 'ngx-model';
import { Observable } from 'rxjs';
import { PosWarning } from '../classes/posWarning';
import { ApiEndpointsService } from './api-endpoints.service';
import { IHttpService } from './ihttp.service';
import { SortDirection } from 'src/app/directives/sortable/sortable.directive';
import { Product } from '../classes/product';
import { Brand } from '../classes/brand';
import { Subbrand } from '../classes/subbrand';
import { Line } from '../classes/line';
import { Format } from '../classes/format';
import { ProductFilter } from '../classes/productFilter';
import { formatNumber } from '@angular/common';
import { toInteger } from '@ng-bootstrap/ng-bootstrap/util/util';

import { registerLocaleData } from '@angular/common';
import es from '@angular/common/locales/es';

interface State {
  sortColumn: string;
  sortDirection: SortDirection;
}

@Injectable()
export class ProductService {
  private model: Model<Product[]>;
  product$: Observable<Product[]>;

  private modelFiltered: Model<Product[]>;
  productFiltered$: Observable<Product[]>;

  private brand: Model<Brand[]>;
  private brand$: Observable<Brand[]>;
  private subbrand: Model<Subbrand[]>;
  private subbrand$: Observable<Subbrand[]>;
  private line: Model<Line[]>;
  private line$: Observable<Line[]>;
  private format: Model<Format[]>;
  private format$: Observable<Format[]>;

  private allPosBrands: Model<Brand[]>;
  private allPosBrands$: Observable<Brand[]>;

  private _state: State = {
    sortColumn: '',
    sortDirection: '',
  };

  constructor(
    private modelFactory: ModelFactory<Product[]>,
    private modelFactoryBrand: ModelFactory<Brand[]>,
    private modelFactorySubbrand: ModelFactory<Subbrand[]>,
    private modelFactoryLine: ModelFactory<Line[]>,
    private modelFactoryFormat: ModelFactory<Format[]>,
    private apiEndpointsService: ApiEndpointsService,
    @Inject('HttpService') private httpService: IHttpService
  ) {
    registerLocaleData(es);

    //Todos los datos
    this.model = this.modelFactory.create([]);
    this.product$ = this.model.data$;

    //Datos filtrados visibles en pantalla
    this.modelFiltered = this.modelFactory.create([]);
    this.productFiltered$ = this.modelFiltered.data$;

    this.brand = this.modelFactoryBrand.create([]);
    this.subbrand = this.modelFactorySubbrand.create([]);
    this.line = this.modelFactoryLine.create([]);
    this.format = this.modelFactoryFormat.create([]);

    this.allPosBrands = this.modelFactoryBrand.create([]);

    this.brand$ = this.brand.data$;
    this.subbrand$ = this.subbrand.data$;
    this.line$ = this.line.data$;
    this.format$ = this.format.data$;
    this.allPosBrands$ = this.allPosBrands.data$;
  }

  getProducts(): Product[] {
    return this.model.get();
  }

  setProducts(data: Product[]) {
    this.model.set(data);
  }

  getProductFiltered(): Product[] {
    return this.modelFiltered.get();
  }

  setProductFiltered(data: Product[]) {
    this.modelFiltered.set(data);
    this.loadProductFilter();
  }

  private getKgFromId(grams: number): string {
    let ret: string = '';
    let kg: number = grams / 1000;
    ret = formatNumber(kg, 'es', '1.0-2');
    ret = ret + ' KG';
    return ret;
  }

  loadProductFilter() {
    //console.log('loadProductFilter');
    let new_brand: Brand[] = [];
    let new_subbrand: Subbrand[] = [];
    let new_line: Line[] = [];
    let new_format: Format[] = [];

    for (let entry of this.modelFiltered.get()) {
      new_brand.push(new Brand(entry.brandId, entry.brandName));
      new_subbrand.push(new Subbrand(entry.subbrandId, entry.subbrandName));
      new_line.push(new Line(entry.lineId, entry.lineName));
      new_format.push(
        new Format(entry.formatId, this.getKgFromId(Number(entry.formatId)))
      );
    }
    this.brand.set(
      new_brand.filter(
        (item, i, arr) => arr.findIndex((t) => t.id === item.id) === i
      )
    );

    this.subbrand.set(
      new_subbrand.filter(
        (item, i, arr) => arr.findIndex((t) => t.id === item.id) === i
      )
    );

    this.line.set(
      new_line.filter(
        (item, i, arr) => arr.findIndex((t) => t.id === item.id) === i
      )
    );

    this.format.set(
      new_format.filter(
        (item, i, arr) => arr.findIndex((t) => t.id === item.id) === i
      )
    );
  }

  getAllPosBrands(): Observable<Brand[]> {
    return this.allPosBrands$;
  }

  getBrand(): Observable<Brand[]> {
    return this.brand$;
  }

  getSubrand(): Observable<Subbrand[]> {
    return this.subbrand$;
  }
  getLine(): Observable<Line[]> {
    return this.line$;
  }
  getFormat(): Observable<Format[]> {
    return this.format$;
  }

  iniProducts(posId: string | null): Observable<Product[]> {
    this.httpService
      .get(this.apiEndpointsService.getProductsEnpoint(posId))
      .subscribe((data) => {
        if (
          data != undefined &&
          data != null &&
          data.value != undefined &&
          data.value != null
        ) {
          let new_data: Product[] = [];
          let brands: Brand[] = [];

          for (let entry of data.value) {
            entry.FORMATNAME = this.getKgFromId(Number(entry.FORMATID));
            new_data.push(new Product(entry));
            brands.push(new Brand(entry.brandId, entry.brandName));
          }

          //distinc de todas las marcas para mostrar / esconder logotipos.
          this.allPosBrands.set(
            brands.filter(
              (item, i, arr) => arr.findIndex((t) => t.id === item.id) === i
            )
          );

          this.model.set(new_data);
          this.modelFiltered.set(new_data);

          this.loadProductFilter();
        }
      });
    return this.productFiltered$;
  }

  filterProducts(filter: ProductFilter) {
    //console.log(' filtro productos ');
    //console.log(filter);
    this.setProductFiltered(
      this.getProducts().filter((p) => {
        let ret = false;
        if (
          filter.brand != '' &&
          filter.subbrand == '' &&
          filter.line == '' &&
          filter.format == ''
        ) {
          ret = p.brandId == filter.brand;
        } else if (
          filter.brand != '' &&
          filter.subbrand != '' &&
          filter.line == '' &&
          filter.format == ''
        ) {
          ret = p.brandId == filter.brand && p.subbrandId == filter.subbrand;
        } else if (
          filter.brand != '' &&
          filter.subbrand != '' &&
          filter.line != '' &&
          filter.format == ''
        ) {
          ret =
            p.brandId == filter.brand &&
            p.subbrandId == filter.subbrand &&
            p.lineId == filter.line;
        } else if (
          filter.brand != '' &&
          filter.subbrand != '' &&
          filter.line != '' &&
          filter.format != ''
        ) {
          ret =
            p.brandId == filter.brand &&
            p.subbrandId == filter.subbrand &&
            p.lineId == filter.line &&
            p.formatId == filter.format;
        } else {
          ret = true;
        }
        return ret;
      })
    );
  }

  set sortColumn(sortColumn: string) {
    this._set({ sortColumn });
  }

  set sortDirection(sortDirection: SortDirection) {
    this._set({ sortDirection });
  }

  private _set(patch: Partial<State>) {
    Object.assign(this._state, patch);
    this.sort();
  }

  private sort() {
    const { sortColumn, sortDirection } = this._state;
    // 1. sort
    this.setProductFiltered(
      sort(this.getProductFiltered(), sortColumn, sortDirection)
    );
  }
}

const compare = (v1: string | number, v2: string | number) =>
  v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

function sort(
  products: Product[],
  column: string,
  direction: string
): Product[] {
  //console.log('----- Ordering products by: ');
  //console.log(direction);
  //console.log(column);
  //console.log(' ---------------------  ');
  if (direction === '' || column === '') {
    return products;
  } else {
    return [...products].sort((a, b) => {
      const res = compare((a as any)[column]!, (b as any)[column]!);
      return direction === 'asc' ? res : -res;
    });
  }
}
